"use client";

import type {
	ChatSessionOptions,
	ChatSession as ChatSessionType,
} from "@whop/embedded-components-vanilla-js/types";
import type { PropsWithChildren } from "react";

import React, {
	createContext,
	useContext,
	useEffect,
	useImperativeHandle,
	useMemo,
} from "react";
import { useElements } from "../provider";

interface ContextState {
	chatSession: ChatSessionType | null;
}

const ChatSessionContext = createContext<ContextState | null>(null);

export function ChatSession({
	children,
	token,
	ref,
}: PropsWithChildren<
	ChatSessionOptions & {
		ref?: React.RefObject<{
			chatSession: ChatSessionType | null;
		} | null>;
	}
>) {
	const elements = useElements();

	const chatSession = useMemo(() => {
		if (!elements) return null;
		return elements.createChatSession({ token });
	}, [elements]);

	useEffect(() => {
		if (!chatSession) return;
		chatSession.updateOptions({ token });
	}, [chatSession, token]);

	const value: ContextState = useMemo(
		() => ({
			chatSession,
		}),
		[chatSession],
	);

	useImperativeHandle(ref, () => ({ chatSession }), [chatSession]);

	return (
		<ChatSessionContext.Provider value={value}>
			{children}
		</ChatSessionContext.Provider>
	);
}

export function useChatSession() {
	const ctx = useContext(ChatSessionContext);
	if (!ctx) {
		throw new Error("useChatSession must be used within a ChatSessionProvider");
	}
	return ctx.chatSession;
}
